// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// What the Election Event > Signatures tab decides without the network: who may
// see and change each part, the rule an action has without a row, whether a
// number of signatures is possible, what a drawer save sends, and how
// certificates and requests are shown and filtered.
import { shownRequestStatus } from "@/lib/signing/status";
import { IPermissions } from "@/types/keycloak";
import { CertificatePostBinding, CertificateRegistration, CrlUnavailablePolicy, ExecutionMode, MAX_SIGNATURES, RequesterSigning, RevocationCheck, SIGNING_ACTIONS, SIGNING_EXPIRY_OPTIONS, SigningAction, SigningActionGroup, SigningRequirement, StaffCertificateStatus, } from "@/lib/signing/types";
export function signaturesAccess(has) {
    var rulesRead = has(IPermissions.SIGNING_RULES_READ);
    var certificatesRead = has(IPermissions.SIGNING_CERTIFICATES_READ);
    var requestsRead = has(IPermissions.SIGNING_REQUESTS_READ);
    var rulesWrite = has(IPermissions.SIGNING_RULES_WRITE);
    var issuersWrite = has(IPermissions.SIGNING_ISSUERS_WRITE);
    var checksWrite = has(IPermissions.SIGNING_CHECKS_WRITE);
    var certificatesRegister = has(IPermissions.SIGNING_CERTIFICATES_REGISTER);
    var certificatesRevoke = has(IPermissions.SIGNING_CERTIFICATES_REVOKE);
    return {
        tab: has(IPermissions.ELECTION_EVENT_SIGNATURES_TAB) &&
            (rulesRead || certificatesRead || requestsRead),
        rulesRead: rulesRead,
        rulesWrite: rulesWrite,
        rolesWrite: rulesWrite && has(IPermissions.ROLE_READ) && has(IPermissions.ROLE_WRITE),
        certificatesRead: certificatesRead,
        issuersWrite: issuersWrite,
        checksWrite: checksWrite,
        certificatesRegister: certificatesRegister,
        certificatesRevoke: certificatesRevoke,
        requestsRead: requestsRead,
        requestsCancel: has(IPermissions.SIGNING_REQUESTS_CANCEL),
        requestsExport: has(IPermissions.SIGNING_REQUESTS_EXPORT),
    };
}
/**
 * After lockdown the rules belong to the configuration version: changing them
 * goes through a new one, so the rules and who signs them are read-only.
 */
export var afterLockdown = function (access, lockedDown) {
    return lockedDown ? __assign(__assign({}, access), { rulesWrite: false, rolesWrite: false }) : access;
};
/** The rule of an action without a row: no signatures needed (design §1). */
export var defaultRule = function (action) { return ({
    action: action,
    requirement: SigningRequirement.NotRequired,
    signatures: 1,
    requester_signing: RequesterSigning.NotAllowed,
    expires_minutes: 60,
    revision: 0,
}); };
export var ruleOf = function (action, rules) { var _a; return (_a = rules.find(function (rule) { return rule.action === action; })) !== null && _a !== void 0 ? _a : defaultRule(action); };
/** The checks of an event without a row (design §1). */
export var DEFAULT_CHECKS = {
    revocation_check: RevocationCheck.Check,
    crl_unavailable: CrlUnavailablePolicy.Refuse,
    registration: CertificateRegistration.OnFirstUse,
    post_binding: CertificatePostBinding.OnePost,
    revision: 0,
};
/** Trustee actions: each trustee signs their own step; the rule only switches on or off. */
export var isTrusteeAction = function (action) {
    return SIGNING_ACTIONS[action].mode === ExecutionMode.Gate;
};
/** The catalog's groups, each with its actions, in catalog order. */
export var actionGroups = function () {
    return Object.values(SigningActionGroup).map(function (group) { return ({
        group: group,
        actions: Object.values(SigningAction).filter(function (action) { return SIGNING_ACTIONS[action].group === group; }),
    }); });
};
/** The key under `signing.expiry` that labels an expiry; `other` for a value the drawer doesn't offer. */
export var expiryKey = function (minutes) {
    return SIGNING_EXPIRY_OPTIONS.includes(minutes) ? String(minutes !== null && minutes !== void 0 ? minutes : "none") : "other";
};
/** The most recent save among the rules, and the name of who saved it when it was stored. */
export function lastChange(rules) {
    return rules.reduce(function (latest, _a) {
        var updated_by_name = _a.updated_by_name, updated_at = _a.updated_at;
        return !latest || Date.parse(updated_at) > Date.parse(latest.updated_at)
            ? { name: updated_by_name !== null && updated_by_name !== void 0 ? updated_by_name : null, updated_at: updated_at }
            : latest;
    }, null);
}
export var SignaturesProblem;
(function (SignaturesProblem) {
    SignaturesProblem["AtLeastOne"] = "at-least-one";
    /** More than the Post with most signers (or the event) can give. */
    SignaturesProblem["TooMany"] = "too-many";
    /** More than any rule may need (`MAX_SIGNATURES`, the server's own maximum). */
    SignaturesProblem["OutOfRange"] = "out-of-range";
})(SignaturesProblem || (SignaturesProblem = {}));
/** The number typed in Signatures needed, or null when it isn't a whole number. */
export function parseSignatures(text) {
    var trimmed = text.trim();
    return /^-?\d+$/.test(trimmed) ? Number(trimmed) : null;
}
/**
 * Checks a number of signatures against what the Posts can give (design §5).
 * The capacity is of the saved roles: when the draft changes them, only the
 * range is checked here and the server checks the rest on save.
 */
export function checkSignatures(signatures, savedCapacity, _a) {
    var _b, _c;
    var _d = _a === void 0 ? {} : _a, _e = _d.rolesChanged, rolesChanged = _e === void 0 ? false : _e, _f = _d.requesterExcluded, requesterExcluded = _f === void 0 ? false : _f;
    var capacity = rolesChanged ? null : savedCapacity;
    var max = (_b = capacity === null || capacity === void 0 ? void 0 : capacity.max) !== null && _b !== void 0 ? _b : null;
    var posts = (_c = capacity === null || capacity === void 0 ? void 0 : capacity.posts) !== null && _c !== void 0 ? _c : [];
    var minSigners = capacity
        ? posts.length
            ? Math.min.apply(Math, posts.map(function (_a) {
                var count = _a.count;
                return count;
            })) : capacity.max
        : null;
    if (signatures === null || signatures < 1) {
        return {
            problem: SignaturesProblem.AtLeastOne,
            max: max,
            shortPosts: [],
            requesterShort: [],
            minSigners: minSigners,
        };
    }
    if (signatures > MAX_SIGNATURES) {
        return {
            problem: SignaturesProblem.OutOfRange,
            max: MAX_SIGNATURES,
            shortPosts: [],
            requesterShort: [],
            minSigners: minSigners,
        };
    }
    if (max !== null && signatures > max) {
        return {
            problem: SignaturesProblem.TooMany,
            max: max,
            shortPosts: [],
            requesterShort: [],
            minSigners: minSigners,
        };
    }
    var shortPosts = [];
    var requesterShort = [];
    var add = function (groups, signers, name) {
        var group = groups.find(function (entry) { return entry.signers === signers; });
        if (group)
            group.posts.push(name);
        else
            groups.push({ signers: signers, posts: [name] });
    };
    for (var _i = 0, posts_1 = posts; _i < posts_1.length; _i++) {
        var _g = posts_1[_i], name_1 = _g.name, signers = _g.count;
        if (signers < signatures)
            add(shortPosts, signers, name_1);
        else if (requesterExcluded && signers - 1 < signatures)
            add(requesterShort, signers - 1, name_1);
    }
    shortPosts.sort(function (a, b) { return a.signers - b.signers; });
    requesterShort.sort(function (a, b) { return a.signers - b.signers; });
    return { problem: null, max: max, shortPosts: shortPosts, requesterShort: requesterShort, minSigners: minSigners };
}
export var draftOf = function (rule, roles) { return ({
    requirement: rule.requirement,
    signatures: String(rule.signatures),
    requesterSigning: rule.requester_signing,
    expiresMinutes: rule.expires_minutes,
    roles: __spreadArray([], roles, true),
}); };
var sameSet = function (a, b) {
    return a.length === b.length && a.every(function (value) { return b.includes(value); });
};
/** The typed number, or the text when it isn't one, so "03" equals "3" but "" differs. */
var typedSignatures = function (text) { var _a; return (_a = parseSignatures(text)) !== null && _a !== void 0 ? _a : text.trim(); };
export var rolesChanged = function (initial, draft) {
    return !sameSet(initial.roles, draft.roles);
};
export var draftChanged = function (initial, draft) {
    return initial.requirement !== draft.requirement ||
        typedSignatures(initial.signatures) !== typedSignatures(draft.signatures) ||
        initial.requesterSigning !== draft.requesterSigning ||
        initial.expiresMinutes !== draft.expiresMinutes ||
        !sameSet(initial.roles, draft.roles);
};
/** The body of `PUT /signing-rules/<action>` for a draft of the rule; roles are group ids. */
export function ruleInputOf(electionEventId, rule, draft, initialRoles) {
    var trustee = isTrusteeAction(rule.action);
    var signatures = parseSignatures(draft.signatures);
    var input = {
        election_event_id: electionEventId,
        action: rule.action,
        requirement: draft.requirement,
        signatures: trustee
            ? 1
            : signatures !== null && signatures >= 1
                ? signatures
                : rule.signatures,
        requester_signing: trustee ? RequesterSigning.Allowed : draft.requesterSigning,
        expires_minutes: trustee ? rule.expires_minutes : draft.expiresMinutes,
        expected_revision: rule.revision,
    };
    // An off rule has no signers to choose.
    if (draft.requirement === SigningRequirement.NotRequired)
        return input;
    var add = draft.roles.filter(function (role) { return !initialRoles.includes(role); });
    var remove = initialRoles.filter(function (role) { return !draft.roles.includes(role); });
    return add.length || remove.length ? __assign(__assign({}, input), { roles: { add: add, remove: remove } }) : input;
}
/** How a registered certificate is shown; "Expires soon" is under 30 days. */
export var CertificateDisplayStatus;
(function (CertificateDisplayStatus) {
    CertificateDisplayStatus["Active"] = "active";
    CertificateDisplayStatus["ExpiresSoon"] = "expires-soon";
    CertificateDisplayStatus["Expired"] = "expired";
    CertificateDisplayStatus["Revoked"] = "revoked";
})(CertificateDisplayStatus || (CertificateDisplayStatus = {}));
export var EXPIRES_SOON_MS = 30 * 24 * 60 * 60 * 1000;
export function certificateStatus(certificate, now) {
    if (certificate.status === StaffCertificateStatus.Revoked) {
        return CertificateDisplayStatus.Revoked;
    }
    var left = Date.parse(certificate.not_after) - now.getTime();
    if (left <= 0)
        return CertificateDisplayStatus.Expired;
    if (left < EXPIRES_SOON_MS)
        return CertificateDisplayStatus.ExpiresSoon;
    return CertificateDisplayStatus.Active;
}
export function filterCertificates(certificates, _a, now, postName) {
    var search = _a.search, status = _a.status;
    var needle = search.trim().toLowerCase();
    return certificates.filter(function (certificate) {
        var _a;
        if (status && certificateStatus(certificate, now) !== status)
            return false;
        if (!needle)
            return true;
        return [
            certificate.username,
            (_a = certificate.user_display_name) !== null && _a !== void 0 ? _a : "",
            certificate.subject,
            certificate.issuer,
            certificate.fingerprint_sha256,
            postName(certificate.election_id),
        ].some(function (value) { return value.toLowerCase().includes(needle); });
    });
}
/** The CN of a distinguished name (RFC 4514 or OpenSSL's slash form), or the name itself. */
export function commonName(dn) {
    var match = /(?:^|[,/+]\s*)CN=((?:\\.|[^,/+])*)/.exec(dn);
    return match ? match[1].trim() : dn;
}
/** "F7:08:0F:24:…:FE:78": the first four and last two bytes of a hex fingerprint. */
export function shortFingerprint(hex) {
    var _a;
    var bytes = (_a = (hex !== null && hex !== void 0 ? hex : "")
        .replace(/[^0-9a-f]/gi, "")
        .toUpperCase()
        .match(/.{2}/g)) !== null && _a !== void 0 ? _a : [];
    if (bytes.length <= 6)
        return bytes.join(":");
    return __spreadArray(__spreadArray(__spreadArray([], bytes.slice(0, 4), true), ["…"], false), bytes.slice(-2), true).join(":");
}
var PEM_HEADER = "-----BEGIN CERTIFICATE-----";
var base64 = function (bytes) {
    var binary = "";
    for (var index = 0; index < bytes.length; index += 1) {
        binary += String.fromCharCode(bytes[index]);
    }
    return btoa(binary);
};
/** An issuer file as `signingImportIssuers` takes it: PEM text, or a .cer/.der's bytes in base64. */
export function issuerUpload(bytes) {
    var text = toPemText(bytes);
    return text ? { pem: text, der_base64: null } : { pem: null, der_base64: base64(bytes) };
}
/** PEM text with OpenSSL's older "X509 CERTIFICATE" label renamed, or null for binary DER. */
var toPemText = function (bytes) {
    var text = new TextDecoder()
        .decode(bytes)
        .replace(/(-----(?:BEGIN|END)) X509 CERTIFICATE-----/g, "$1 CERTIFICATE-----");
    return text.includes(PEM_HEADER) ? text : null;
};
/**
 * A certificate file's contents as PEM: PEM text as it is (OpenSSL's older
 * "X509 CERTIFICATE" label renamed), DER (.cer) bytes wrapped.
 */
export function toPem(bytes) {
    var _a;
    var text = toPemText(bytes);
    if (text)
        return text;
    var lines = (_a = base64(bytes).match(/.{1,64}/g)) !== null && _a !== void 0 ? _a : [];
    return "".concat(PEM_HEADER, "\n").concat(lines.join("\n"), "\n-----END CERTIFICATE-----\n");
}
/** The status a request shows, as the signing panel shows it. */
export var requestStatus = shownRequestStatus;
export var filterRequests = function (requests, status, now) { return requests.filter(function (request) { return !status || requestStatus(request, now) === status; }); };
export function lastSignature(request) {
    return request.approvals.reduce(function (latest, approval) {
        return !latest || Date.parse(approval.signed_at) > Date.parse(latest.signed_at)
            ? approval
            : latest;
    }, null);
}
/** A person's display name, or their username when the row has none. */
export var personName = function (name, username) {
    return (name === null || name === void 0 ? void 0 : name.trim()) ? name : username;
};
/** The DER bytes of the first certificate of a PEM, or null when it has none. */
export function pemToDer(pem) {
    var _a;
    var body = (_a = /-----BEGIN CERTIFICATE-----([^-]*)-----END CERTIFICATE-----/.exec(pem)) === null || _a === void 0 ? void 0 : _a[1];
    if (!body)
        return null;
    try {
        var binary = atob(body.replace(/\s/g, ""));
        return Uint8Array.from(binary, function (character) { return character.charCodeAt(0); });
    }
    catch (_b) {
        return null;
    }
}
/** The certificate's `fingerprint_sha256`: SHA-256 of its DER, lowercase hex. */
export function certificateFingerprint(pem) {
    return __awaiter(this, void 0, void 0, function () {
        var der, digest;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    der = pemToDer(pem);
                    if (!der)
                        return [2 /*return*/, null];
                    return [4 /*yield*/, crypto.subtle.digest("SHA-256", der)];
                case 1:
                    digest = _a.sent();
                    return [2 /*return*/, Array.from(new Uint8Array(digest), function (byte) { return byte.toString(16).padStart(2, "0"); }).join("")];
            }
        });
    });
}
/**
 * The account to link a registration to (decided O1): the one an active
 * registration of the same certificate belongs to, or the first account it
 * already links.
 */
export function linkTarget(certificates, fingerprint) {
    var _a;
    var holder = certificates.find(function (certificate) {
        return certificate.status === StaffCertificateStatus.Active &&
            certificate.fingerprint_sha256 === fingerprint;
    });
    return holder ? ((_a = holder.linked_to) !== null && _a !== void 0 ? _a : holder.user_id) : null;
}
/** The error codes of the signing routes (the contract in lib/signing/api.ts), as the portal handles them. */
export var SigningErrorCode;
(function (SigningErrorCode) {
    SigningErrorCode["Forbidden"] = "forbidden";
    SigningErrorCode["Invalid"] = "invalid";
    /** A stale expected revision: someone else saved first. */
    SigningErrorCode["Conflict"] = "conflict";
    SigningErrorCode["NotFound"] = "not-found";
    /** A certificate check refused: `check` names it. */
    SigningErrorCode["Refused"] = "signing-refused";
    /** The event is locked down (PR 5 sends it as `invalid`; a distinct code is also read). */
    SigningErrorCode["LockedDown"] = "locked-down";
    SigningErrorCode["Other"] = "other";
})(SigningErrorCode || (SigningErrorCode = {}));
var CODES = {
    "forbidden": SigningErrorCode.Forbidden,
    "invalid": SigningErrorCode.Invalid,
    "conflict": SigningErrorCode.Conflict,
    "stale-revision": SigningErrorCode.Conflict,
    "not-found": SigningErrorCode.NotFound,
    "signing-refused": SigningErrorCode.Refused,
    "already-signed": SigningErrorCode.Refused,
    "locked-down": SigningErrorCode.LockedDown,
};
var STATUSES = {
    400: SigningErrorCode.Invalid,
    403: SigningErrorCode.Forbidden,
    404: SigningErrorCode.NotFound,
    409: SigningErrorCode.Conflict,
    422: SigningErrorCode.Refused,
};
var objectOf = function (value) {
    return value && typeof value === "object" ? value : {};
};
/** Classifies a failed action by `extensions.code`, falling back to the HTTP status. */
export function signingError(error) {
    var errors = objectOf(error).graphQLErrors;
    for (var _i = 0, _a = Array.isArray(errors) ? errors : []; _i < _a.length; _i++) {
        var graphQLError = _a[_i];
        var record = objectOf(graphQLError);
        var extensions = objectOf(record.extensions);
        var detail = typeof extensions.reason === "string" ? { reason: extensions.reason } : {};
        var check = typeof extensions.check === "string" ? extensions.check : null;
        var holder = typeof extensions.user_id === "string"
            ? {
                userId: extensions.user_id,
                name: typeof extensions.display_name === "string"
                    ? extensions.display_name
                    : null,
            }
            : undefined;
        var code = typeof extensions.code === "string" ? CODES[extensions.code] : undefined;
        if (code)
            return holder ? __assign({ code: code, check: check, holder: holder }, detail) : __assign({ code: code, check: check }, detail);
        var status_1 = objectOf(objectOf(extensions.internal).response).status;
        if (typeof status_1 === "number" && STATUSES[status_1])
            return __assign({ code: STATUSES[status_1], check: check }, detail);
    }
    return { code: SigningErrorCode.Other, check: null };
}
/**
 * The message of a refused write, or null for the write's own fallback. The
 * server refuses a rule edit of a locked-down event as invalid input.
 */
export function errorKey(code, lockedDown, reason) {
    switch (code) {
        case SigningErrorCode.Forbidden:
            return "signing.errors.forbidden";
        case SigningErrorCode.Invalid:
            if (lockedDown)
                return "signing.errors.lockedDown";
            return reason === "automated-ceremonies"
                ? "signing.errors.automatedCeremonies"
                : "signing.errors.invalid";
        case SigningErrorCode.LockedDown:
            return "signing.errors.lockedDown";
        case SigningErrorCode.Conflict:
            return "signing.errors.conflict";
        case SigningErrorCode.NotFound:
            return "signing.errors.notFound";
        default:
            return null;
    }
}
/** Display roles by name in the UI language, without changing their identity or source order. */
export function sortRolesByName(roles, language) {
    var collator = new Intl.Collator(language, { numeric: true, sensitivity: "base" });
    return __spreadArray([], roles, true).sort(function (left, right) { return collator.compare(left.name, right.name) || left.id.localeCompare(right.id); });
}
